import { FormGroup, Icon, Intent, Position } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import * as React from 'react';
import {
  GenericProperty,
  GenericPropertySuggestion,
  GenericPropertySuggestionType,
  ManagedGenericPropertiesEditor,
  StringInput,
  Tooltip,
} from 'vet-bones/components';
import { isEqual } from 'vet-bones/utils';

import { AdvancedOptions } from 'src/components/AdvancedOptions';
import {
  DataSourceOption,
  JDBC_DRIVER_KEY,
  STANDARD_JDBC_PROPERTY_KEYS,
  defaultTypeDescription,
} from 'src/constants/dataSourceTypeDescription';
import { MAX_PROPERTY_SUGGESTION_ITEMS } from 'src/constants/limits';
import { DATA_SOURCE_OPTIONS_DOCUMENTATION_LINK } from 'src/constants/links';
import {
  TEST_IDS,
  getDataSourceCreationPanelAdvancedPropertyTestId,
  getDataSourceStandardOptionTestId,
} from 'src/constants/testIds';
import { useValidateDataSourceName } from 'src/containers/ProjectResourceDialog/ProjectFileUploadCreationPanel/hooks';
import { connectionSelector } from 'src/selectors/connection';
import { dataSourcePropertiesSelector } from 'src/selectors/dataSourceProperties';
import * as copy from 'src/templates/copy';
import {
  countUniqueExistingProperties,
  genericPropertyValidator,
  isRowInvalid,
} from 'src/utils/genericPropertyValidation';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

export const DATA_SOURCE_NAME = 'DATA_SOURCE_NAME';

type StandardPropertyFieldProps = {
  description?: string;
  label: string;
  setValue: (value: string) => void;
  testId: string;
  errorValue?: string;
  value: string;
};

const StandardPropertyField: React.VFC<StandardPropertyFieldProps> = ({
  description,
  label,
  errorValue,
  setValue,
  testId,
  value,
}) => {
  const toolTipContent = (value && errorValue) || description || '';
  const [isToolTipOpen, setIsToolTipOpen] = React.useState(false);
  const isObfuscated =
    toolTipContent ===
    defaultTypeDescription.DATA_SOURCE_OPTIONS[DataSourceOption.PASSWORD]
      .description;

  return (
    <div
      className="sd-data-source-panel-properties__standard-property"
      onMouseEnter={() => setIsToolTipOpen(true)}
      onMouseLeave={() => setIsToolTipOpen(false)}
    >
      <FormGroup inline label={label}>
        <StringInput
          intent={value && errorValue ? Intent.DANGER : Intent.NONE}
          // Obfuscate text for password
          onSetValue={setValue}
          testId={testId}
          type={isObfuscated ? 'password' : 'text'}
          value={value}
        />
        <Tooltip
          content={toolTipContent}
          disabled={!toolTipContent}
          fill
          isOpen={isToolTipOpen}
          position={Position.BOTTOM}
        />
      </FormGroup>
    </div>
  );
};

type DataSourceCreationFormProps = {
  advancedProperties: GenericProperty[];
  dataSourceName: string;
  standardProperties: Record<string, string>;
  type: string;
  onUpdateAdvancedProperties: (advancedProperties: GenericProperty[]) => void;
  onUpdateStandardProperties: (
    standardProperties: Record<string, string>
  ) => void;
  onUpdateName: (name: string) => void;
};

export const DataSourceCreationForm: React.VFC<DataSourceCreationFormProps> = ({
  advancedProperties,
  dataSourceName,
  onUpdateAdvancedProperties,
  onUpdateName,
  onUpdateStandardProperties,
  standardProperties,
  type,
}) => {
  const connection = useReduxSelector(connectionSelector);
  const propertyDescriptions = useReduxSelector(
    dataSourcePropertiesSelector(connection, type),
    isEqual
  );

  const advancedPropertySuggestions = React.useMemo<
    GenericPropertySuggestion[]
  >(() => {
    const existingAdvancedProperties: string[] =
      Object.values(DataSourceOption);
    return Object.entries(propertyDescriptions)
      .filter(
        ([property]) =>
          !(property in DataSourceOption) &&
          property !== JDBC_DRIVER_KEY &&
          !existingAdvancedProperties.includes(property)
      )
      .map(([property, propertyDescription]) => {
        const suggestion: GenericPropertySuggestion = {
          name: property,
          description: propertyDescription?.description,
          defaultValue:
            propertyDescription?.default !== undefined
              ? `${propertyDescription?.default}`
              : undefined,
          type:
            (propertyDescription?.type?.toUpperCase() as GenericPropertySuggestionType) ||
            GenericPropertySuggestionType.STRING,
        };
        return suggestion;
      });
  }, [propertyDescriptions]);
  const [
    hasAttemptedToCreateInvalidNewRow,
    setHasAttemptedToCreateInvalidNewRow,
  ] = React.useState(false);

  const validateName = useValidateDataSourceName();

  const dataSourceNameError = validateName(dataSourceName);

  return (
    <div className="sd-data-source-panel-properties">
      <div className="sd-data-source-panel-properties__standard-properties">
        <StandardPropertyField
          errorValue={dataSourceNameError}
          label={
            copy.components.projectResourceDialog.projectDataSourceCreationPanel
              .dataSourceNameLabel
          }
          setValue={onUpdateName}
          testId={getDataSourceStandardOptionTestId(DATA_SOURCE_NAME)}
          value={dataSourceName}
        />
        {(
          Object.entries(DataSourceOption) as Array<
            [keyof typeof DataSourceOption, string]
          >
        ).map(([propertyType, property]) => (
          <StandardPropertyField
            key={property}
            description={propertyDescriptions[property]?.description}
            label={
              copy.components.projectResourceDialog
                .projectDataSourceCreationPanel.propertyLabels[propertyType]
            }
            setValue={(value: string) =>
              onUpdateStandardProperties({
                ...standardProperties,
                [property]: value,
              })
            }
            testId={getDataSourceStandardOptionTestId(property)}
            value={standardProperties[property]}
          />
        ))}
      </div>
      <AdvancedOptions
        buttonTestId={
          TEST_IDS.projectResourceDialog.dataSourceCreationPanel
            .advancedPropertiesButton
        }
        rightElement={
          <a
            className="sd-data-source-panel-properties__advanced-docs-link"
            href={DATA_SOURCE_OPTIONS_DOCUMENTATION_LINK}
            rel="noreferrer"
            target="_blank"
          >
            <Icon icon={IconNames.INFO_SIGN} />
          </a>
        }
        text={() =>
          copy.components.projectResourceDialog.projectDataSourceCreationPanel.advancedOptions(
            advancedProperties.length
          )
        }
      >
        <ManagedGenericPropertiesEditor
          editorTestId={
            TEST_IDS.projectResourceDialog.dataSourceCreationPanel
              .advancedPropertiesEditor
          }
          maxSuggestions={MAX_PROPERTY_SUGGESTION_ITEMS}
          onPropertiesUpdated={onUpdateAdvancedProperties}
          preventAddingNewProperty={({ name, value }) => {
            const invalid = isRowInvalid(
              name,
              value,
              STANDARD_JDBC_PROPERTY_KEYS,
              countUniqueExistingProperties(advancedProperties, name)
            );
            setHasAttemptedToCreateInvalidNewRow(invalid);
            return invalid;
          }}
          properties={advancedProperties}
          propertyActionTestId={
            TEST_IDS.projectResourceDialog.dataSourceCreationPanel
              .advancedProperties.addButton
          }
          propertyNameTestId={
            TEST_IDS.projectResourceDialog.dataSourceCreationPanel
              .advancedProperties.newName
          }
          propertySuggestions={advancedPropertySuggestions}
          propertyValueTestId={
            TEST_IDS.projectResourceDialog.dataSourceCreationPanel
              .advancedProperties.newValue
          }
          rowTestId={getDataSourceCreationPanelAdvancedPropertyTestId}
          validator={genericPropertyValidator(
            hasAttemptedToCreateInvalidNewRow,
            STANDARD_JDBC_PROPERTY_KEYS,
            advancedProperties
          )}
        />
      </AdvancedOptions>
    </div>
  );
};
